import { useState } from "react";
import { Link } from "react-router-dom";
import ScrollReveal from "@/components/base/ScrollReveal";
import TestimonialsCarousel from "@/pages/our-product/components/TestimonialsCarousel";
import SuccessPopup from "@/components/feature/SuccessPopup";

const services = [
  {
    icon: "ri-shield-user-line",
    title: "Cybersecurity Consulting",
    description: "Expert security assessments and strategic consulting to identify vulnerabilities and strengthen your digital infrastructure.",
  },
  {
    icon: "ri-smartphone-line",
    title: "Mobile App Development",
    description: "Custom mobile applications built with security-first architecture across all platforms.",
  },
  {
    icon: "ri-cloud-line",
    title: "Cloud Solutions",
    description: "Secure cloud migration and management services that optimize performance while maintaining enterprise-grade security.",
  },
  {
    icon: "ri-code-box-line",
    title: "Web Development",
    description: "Modern web solutions built with cutting-edge frameworks, designed for speed, security, and scalability.",
  },
  {
    icon: "ri-fingerprint-line",
    title: "Identity Management",
    description: "Comprehensive identity and access management solutions that protect your organization's sensitive data.",
  },
  {
    icon: "ri-brain-line",
    title: "AI Integration",
    description: "Intelligent automation and AI-powered solutions that enhance productivity while maintaining robust security.",
  },
];

const productFeatures = [
  "VPN — 1000+ Secure Servers",
  "AdBlock — Block Ads & Trackers",
  "Check IP — Instant Connection Info",
  "Speed Test — Real-Time Speed Check",
];

const workSteps = [
  {
    icon: "ri-search-line",
    title: "Discovery & Planning",
    description: "We research user needs and identify opportunities for innovative mobile privacy solutions.",
  },
  {
    icon: "ri-draft-line",
    title: "Design & Architecture",
    description: "Our experts craft intuitive app designs and robust backend architecture for seamless performance.",
  },
  {
    icon: "ri-tools-line",
    title: "Development & Testing",
    description: "We build and rigorously test our apps to ensure security, speed, and reliability on every device.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Launch & Continuous Updates",
    description: "We release our apps with regular updates and 24/7 support to keep our users protected.",
  },
];

export default function HomePage() {
  const [loading, setLoading] = useState(false);
  const [errorSubmit, setErrorSubmit] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [formErrors, setFormErrors] = useState({ name: '', email: '', message: '' });

  const validate = () => {
    const errors = { name: '', email: '', message: '' };
    if (formData.name.trim().length < 3) errors.name = 'Name must be at least 3 characters';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Enter a valid email address';
    }
    if (formData.message.trim().length < 20) errors.message = 'Message must be at least 20 characters';
    setFormErrors(errors);
    return !errors.name && !errors.email && !errors.message;
  };

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setErrorSubmit(false);

    let text = '';
    for (const [key, value] of Object.entries(formData)) {
      text += `${key} - ${value}\n`;
    }

    const payload = {
      email: formData.email,
      text,
      problemType: 'General Inquiry',
      to_email: 'support@app-for-security.com',
      subject: 'Contact Request',
    };

    try {
      const res = await fetch('https://rocknlabs.com/api/support/request_form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Server error');
      setIsSuccessOpen(true);
      setFormData({ name: '', email: '', message: '' });
      setFormErrors({ name: '', email: '', message: '' });
    } catch {
      setErrorSubmit(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      {/* Hero Section */}
      <section className="relative h-[600px] md:h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/ee0ff265c62d3a2287b67a9d731587ad/e9f36c69d5c8496221fd9562bd7c5267.jpeg"
            alt="Cybersecurity Solutions"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto w-full">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 font-heading drop-shadow-lg">
            Protect Your Digital Life Effortlessly
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-10 leading-relaxed">
            Next-generation privacy and VPN solutions for your mobile devices. Browse securely, block ads, and protect your data anywhere in the world.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/our-product"
              className="bg-primary-500 text-white font-medium px-8 py-4 rounded-lg hover:bg-primary-600 transition-colors text-center whitespace-nowrap"
            >
              Explore Online Shield
            </Link>
            <Link
              to="/contact"
              className="bg-white/10 backdrop-blur-sm text-white font-medium px-8 py-4 rounded-lg hover:bg-white/20 transition-colors border border-white/20 text-center whitespace-nowrap"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 md:py-16 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-2">1000+</div>
              <p className="text-foreground-600 text-sm">Secure VPN Servers</p>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-2">4-in-1</div>
              <p className="text-foreground-600 text-sm">Privacy Tools</p>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-2">iOS</div>
              <p className="text-foreground-600 text-sm">Native App</p>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-2">24/7</div>
              <p className="text-foreground-600 text-sm">Expert Support</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <ScrollReveal>
              <div className="text-center mb-10 md:mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-4 font-heading">About Us</h2>
                <p className="text-lg text-foreground-600 max-w-3xl mx-auto">
                  Leading technology company specializing in mobile privacy and digital protection solutions
                </p>
              </div>
            </ScrollReveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
              <ScrollReveal delay={100}>
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4 font-heading">Who We Are</h3>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    Web 3.0 Soft Limited is a leading technology company specializing in next-generation mobile
                    applications and digital privacy solutions. Founded with a vision to empower users with control
                    over their online presence, we have grown to become a trusted developer of privacy-focused apps.
                  </p>
                  <p className="text-foreground-600 leading-relaxed mb-6">
                    Our team of skilled engineers and privacy specialists brings together extensive experience in
                    network security, mobile development, and user-centric design.
                  </p>
                  <Link
                    to="/about-us"
                    className="inline-flex items-center text-primary-500 font-medium hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    Learn More About Us
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={200}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 bg-white p-6 rounded-xl border border-background-200 text-center flex flex-col items-center justify-center min-h-[180px]">
                    <div className="w-14 h-14 bg-primary-500 rounded-full flex items-center justify-center mb-4">
                      <i className="ri-award-line text-white text-2xl"></i>
                    </div>
                    <h4 className="font-semibold text-foreground-900 text-base mb-2">Excellence</h4>
                    <p className="text-foreground-600 text-sm leading-relaxed">Highest quality solutions</p>
                  </div>
                  <div className="flex-1 bg-white p-6 rounded-xl border border-background-200 text-center flex flex-col items-center justify-center min-h-[180px]">
                    <div className="w-14 h-14 bg-primary-500 rounded-full flex items-center justify-center mb-4">
                      <i className="ri-lightbulb-line text-white text-2xl"></i>
                    </div>
                    <h4 className="font-semibold text-foreground-900 text-base mb-2">Innovation</h4>
                    <p className="text-foreground-600 text-sm leading-relaxed">Pioneering new technologies</p>
                  </div>
                  <div className="flex-1 bg-white p-6 rounded-xl border border-background-200 text-center flex flex-col items-center justify-center min-h-[180px]">
                    <div className="w-14 h-14 bg-primary-500 rounded-full flex items-center justify-center mb-4">
                      <i className="ri-shield-line text-white text-2xl"></i>
                    </div>
                    <h4 className="font-semibold text-foreground-900 text-base mb-2">Trust</h4>
                    <p className="text-foreground-600 text-sm leading-relaxed">Reliable protection always</p>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services Section */}
      <section className="py-14 md:py-20 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-4 font-heading">Our Services</h2>
              <p className="text-lg text-foreground-600 max-w-3xl mx-auto">
                Comprehensive technology solutions designed to drive growth and secure your digital assets
              </p>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-stretch">
            {services.map((service, idx) => (
              <ScrollReveal key={idx} delay={idx * 100}>
                <div className="bg-background-50 p-6 md:p-8 rounded-xl border border-background-200 hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
                  <div className="w-14 h-14 bg-primary-500 rounded-xl flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground-900 mb-3">{service.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed flex-grow">{service.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/our-services"
              className="inline-flex items-center bg-primary-500 text-white font-medium px-8 py-3 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              View All Services
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Our Product Section */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-4 font-heading">Our Product</h2>
              <p className="text-lg text-foreground-600 max-w-3xl mx-auto">
                Online Shield — Fast VPN Proxy — privacy and speed for your iPhone
              </p>
            </div>
          </ScrollReveal>
          <div className="bg-white rounded-2xl p-6 md:p-8 lg:p-12 border border-background-200">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
              <ScrollReveal delay={100}>
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-6 font-heading">
                    Complete Mobile Protection
                  </h3>
                  <p className="text-base md:text-lg text-foreground-700 mb-6 leading-relaxed">
                    Online Shield — Fast VPN Proxy is our flagship iOS privacy app developed by Web 3.0 Soft Limited.
                    It combines four powerful tools: VPN with 1000+ secure servers, AdBlock, Check IP, and Speed Test.
                    Browse securely, block ads, and protect your data anywhere in the world.
                  </p>
                  <div className="space-y-4 mb-8">
                    {productFeatures.map((feature, idx) => (
                      <div key={idx} className="flex items-center">
                        <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          <i className="ri-check-line text-white text-sm"></i>
                        </div>
                        <span className="text-foreground-700 font-medium text-sm md:text-base">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/our-product"
                    className="inline-flex items-center bg-primary-500 text-white font-medium px-8 py-3 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    Explore Online Shield
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={200}>
                <div className="relative flex items-center justify-center">
                  <img
                    src="https://storage.readdy-site.link/project_files/957ceb93-2e28-41dc-87f9-7d664dd26839/3e0b551f-40c7-4727-b50c-7074a9c95890_image-18.png"
                    alt="Online Shield App Icon"
                    className="rounded-2xl object-contain w-[280px] h-[280px] md:w-[320px] md:h-[320px]"
                  />
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Our Work Process Section */}
      <section className="py-14 md:py-20 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-4 font-heading">Our Work Process</h2>
              <p className="text-lg text-foreground-600 max-w-3xl mx-auto">
                A streamlined approach to delivering exceptional security solutions
              </p>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {workSteps.map((step, idx) => (
              <ScrollReveal key={idx} delay={idx * 100}>
                <div className="relative text-center p-6">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center text-white text-sm font-bold z-10">
                    {idx + 1}
                  </div>
                  <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${step.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground-900 mb-3">{step.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{step.description}</p>
                  {idx < workSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-20 -right-4 w-8 h-px bg-primary-300"></div>
                  )}
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Client Testimonials Section */}
      <ScrollReveal>
        <TestimonialsCarousel />
      </ScrollReveal>

      {/* Contact Now Section */}
      <section className="relative py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-4 font-heading">Contact Now</h2>
              <p className="text-lg text-foreground-600 max-w-3xl mx-auto">
                Have a question or need assistance? Reach out and our team will get back to you promptly.
              </p>
            </div>
          </ScrollReveal>
          <div className="max-w-xl mx-auto">
            <ScrollReveal delay={200}>
              <div className="bg-white rounded-xl p-6 md:p-8 border border-background-200">
                <form
                  id="contact-form-home"
                  onSubmit={handleSubmit}
                >
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="home-name" className="block text-sm font-medium text-foreground-700 mb-1">Name</label>
                      <input
                        id="home-name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => { setFormData((prev) => ({ ...prev, name: e.target.value })); setFormErrors((prev) => ({ ...prev, name: '' })); }}
                        className={`w-full px-4 py-3 rounded-lg border bg-white text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent ${formErrors.name ? 'border-red-400' : 'border-background-200'}`}
                        placeholder="Your name"
                      />
                      {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="home-email" className="block text-sm font-medium text-foreground-700 mb-1">Email</label>
                      <input
                        id="home-email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => { setFormData((prev) => ({ ...prev, email: e.target.value })); setFormErrors((prev) => ({ ...prev, email: '' })); }}
                        className={`w-full px-4 py-3 rounded-lg border bg-white text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent ${formErrors.email ? 'border-red-400' : 'border-background-200'}`}
                        placeholder="your@email.com"
                      />
                      {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
                    </div>
                    <div>
                      <label htmlFor="home-message" className="block text-sm font-medium text-foreground-700 mb-1">Message</label>
                      <textarea
                        id="home-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        value={formData.message}
                        onChange={(e) => { setFormData((prev) => ({ ...prev, message: e.target.value })); setFormErrors((prev) => ({ ...prev, message: '' })); }}
                        className={`w-full px-4 py-3 rounded-lg border bg-white text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none ${formErrors.message ? 'border-red-400' : 'border-background-200'}`}
                        placeholder="How can we help you?"
                      />
                      <p className="text-xs text-foreground-500 mt-1">Maximum 500 characters</p>
                      {formErrors.message && <p className="text-xs text-red-500 mt-1">{formErrors.message}</p>}
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-primary-500 text-white font-medium py-3 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {loading ? "Sending..." : "Send Message"}
                    </button>
                    {errorSubmit && (
                      <p className="text-sm text-red-600 text-center">Something went wrong. Please try again.</p>
                    )}
                  </div>
                </form>
              </div>
            </ScrollReveal>
          </div>
        </div>

        {isSuccessOpen && (
            <SuccessPopup onClose={() => setIsSuccessOpen(false)} />
         )}
      </section>
    </main>
  );
}